import { formAnswers, taskFieldSnapshot } from './taskFieldSnapshot';

/**
 * Utility class for parsing form field data from task submissions
 * Handles the enhanced headers format: "sequence:fieldId:fieldName:fieldType"
 * Provides backward compatibility with legacy header formats
 */
// An answer nobody gave: missing, or text that is only whitespace. A boolean
// `false` or a number `0` is an answer (a checkbox left unticked, a count of
// nothing). QR one-off reports (#850) store checkbox answers as JSON booleans,
// and reading `false` as blank showed "not filled" where a cross belongs.
const isBlankAnswer = (value) => value === null || value === undefined || String(value).trim() === '';

// Bookkeeping answers the phone writes under a leading underscore that a
// reader can use, by name (api task_field_snapshot.INTERNAL_ANSWER_LABELS).
// Every other underscore key is left out.
const INTERNAL_ANSWER_LABELS = {
  _issue_number: 'Issue',
  _issue_update_status: 'Issue status',
  _issue_update_note: 'Update note',
};

class FormFieldParser {
  /**
   * Parse task form data into structured format.
   *
   * The task describes itself (product owner, 2026-10-18): labels, types and
   * order come from the task's own record of its form (`taskFieldSnapshot`,
   * the same rule as the API, the exports and the emails), in the template's
   * source language whatever language the operator read. `templateFields`
   * (the template the task was filled on) only fills in what the task does
   * not carry: an older phone's task, one created through the API, a
   * migrated one.
   *
   * @param {Object} task - Task object with keyValueMap/formData, headers, field_snapshot
   * @param {Array} [templateFields] - The task's form template fields[]
   * @returns {Object} Parsed form data structure
   */
  static parseTaskFormData(task, templateFields = null) {
    if (!task) return null;

    const { headers, fieldSequence, templateName, template_name } = task;

    // Resolve template name from both camelCase and snake_case fields
    const resolvedTemplateName = templateName || template_name;

    const parsedHeaders = this.parseHeaders(headers);
    // Every field the task says its form had, answered or not: a field left
    // blank still shows, as "Not filled".
    const dataSource = { ...formAnswers(task) };
    Object.keys(parsedHeaders).forEach((fieldId) => {
      if (!(fieldId in dataSource)) dataSource[fieldId] = '';
    });
    (Array.isArray(task.field_snapshot) ? task.field_snapshot : []).forEach((entry) => {
      const id = entry?.id;
      if (typeof id === 'string' && id && !(id in dataSource)) dataSource[id] = '';
    });

    if (Object.keys(dataSource).length === 0) {
      return null;
    }

    const entries = taskFieldSnapshot({ ...task, keyValueMap: dataSource }, templateFields);
    const internal = Object.keys(INTERNAL_ANSWER_LABELS)
      .filter((key) => key in dataSource)
      .map((key) => ({ key, label: INTERNAL_ANSWER_LABELS[key], type: '' }));
    const shown = [...entries, ...internal];

    const parsedFormData = {
      templateName: resolvedTemplateName || 'Unknown Template',
      fields: [],
      hasFieldSequence: Boolean(fieldSequence && fieldSequence.length > 0),
      totalFields: shown.length,
      completedFields: shown.filter((entry) => !isBlankAnswer(dataSource[entry.key])).length,
    };

    shown.forEach((entry, position) => {
      const value = dataSource[entry.key];
      parsedFormData.fields.push({
        id: entry.key,
        // The key itself only when nothing better names it; then a key the
        // builder generated is tidied up (see labelFromFieldKey).
        name: entry.label && entry.label !== entry.key ? entry.label : this.labelFromFieldKey(entry.key),
        // The label the operator read, when it was a translation.
        shownName: entry.shownLabel || null,
        type: entry.type || 'text',
        value,
        sequence: position,
        isEmpty: isBlankAnswer(value),
        isRequired: false // This could be enhanced with template data
      });
    });

    return parsedFormData;
  }

  /**
   * Build a field-id → label map from a form template's fields[].
   * Tolerates both id/field_id and label/name spellings.
   * @param {Array} templateFields - fields[] from the form template
   * @returns {Object} Map of field id to label
   */
  static buildTemplateLabelMap(templateFields) {
    const map = {};
    if (!Array.isArray(templateFields)) return map;
    templateFields.forEach(field => {
      const id = field?.id || field?.field_id;
      const label = field?.label || field?.name;
      if (id && label) map[id] = label;
    });
    return map;
  }

  /**
   * Parse headers array into structured field metadata
   * @param {Array} headers - Headers array from task
   * @returns {Object} Parsed header data keyed by field ID
   */
  static parseHeaders(headers) {
    const parsed = {};
    
    if (!headers || !Array.isArray(headers)) {
      return parsed;
    }

    headers.forEach(header => {
      if (typeof header !== 'string') return;
      
      // Parse enhanced format: "sequence:fieldId:fieldName:fieldType"
      const enhancedMatch = header.match(/^(\d+):([^:]+):([^:]+):(.+)$/);
      if (enhancedMatch) {
        const [, sequence, fieldId, fieldName, fieldType] = enhancedMatch;
        parsed[fieldId] = {
          sequence: parseInt(sequence, 10),
          name: fieldName,
          type: fieldType
        };
        return;
      }
      
      // Legacy format: "fieldId:fieldName" 
      const legacyMatch = header.match(/^([^:]+):(.+)$/);
      if (legacyMatch) {
        const [, fieldId, fieldName] = legacyMatch;
        parsed[fieldId] = {
          sequence: 999, // Default high sequence for legacy
          name: fieldName,
          type: 'text' // Default type
        };
        return;
      }
      
      // Fallback: treat the whole header as field ID with friendly name
      if (header.startsWith('field_')) {
        const friendlyName = this.generateFriendlyFieldName(header);
        parsed[header] = {
          sequence: 999,
          name: friendlyName,
          type: 'text'
        };
      }
    });

    return parsed;
  }
  
  /**
   * The label for a data key that neither the headers nor the template named.
   *
   * A key is usually already the label — legacy SSS stored each answer under
   * its field's own name, so a migrated task's `formData` reads
   * `{Subject: "LS", Tracie: "FU7-314-80-83-6"}`. Only a key the *builder*
   * generated (`field_1754098032970`) says nothing and is worth guessing at.
   *
   * What this replaced guessed the label from the **value**: a short,
   * single-word, letters-only answer became the field's name. So a task whose
   * "Subject" was `LS` rendered as **LS · LS**, and the next task, answered
   * `JA`, rendered as **JA · JA** — the same field wearing a different name on
   * every card, with the key it actually had thrown away. A label that changes
   * with the answer is not a label.
   *
   * @param {string} fieldId - the data key
   * @returns {string} the label to show
   */
  static labelFromFieldKey(fieldId) {
    const key = String(fieldId ?? '').trim();
    if (!key) return '';
    // Builder-generated ids: `field_<timestamp>`, or a long opaque hex/uuid.
    const generated = /^field_\d+$/.test(key)
      || /^[0-9a-f]{20,}$/i.test(key)
      || /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(key);
    if (generated) return this.generateFriendlyFieldName(key);
    return key.replace(/_/g, ' ');
  }

  /**
   * Generate a friendly field name from field ID
   * @param {string} fieldId - Field ID like "field_1754098032970"
   * @returns {string} Friendly name
   */
  static generateFriendlyFieldName(fieldId) {
    // Common field patterns with meaningful names
    const fieldPatterns = {
      'hello': 'Greeting',
      'name': 'Name',
      'description': 'Description',
      'comment': 'Comment', 
      'notes': 'Notes',
      'quantity': 'Quantity',
      'date': 'Date',
      'time': 'Time',
      'location': 'Location',
      'operator': 'Operator',
      'customer': 'Customer',
      'status': 'Status',
      'priority': 'Priority'
    };
    
    // Check if field ID contains recognizable patterns
    const lowerFieldId = fieldId.toLowerCase();
    for (const [pattern, name] of Object.entries(fieldPatterns)) {
      if (lowerFieldId.includes(pattern)) {
        return name;
      }
    }
    
    // Extract timestamp and create sequential field names
    const timestampMatch = fieldId.match(/field_(\d+)/);
    if (timestampMatch) {
      const timestamp = parseInt(timestampMatch[1]);
      // Create more meaningful field names based on position
      const fieldNames = ['Name', 'Description', 'Quantity', 'Date', 'Time', 'Notes', 'Status', 'Location'];
      const index = Math.abs(timestamp) % fieldNames.length;
      return fieldNames[index];
    }
    
    // Final fallback
    return fieldId.replace(/^field_/, 'Field ').replace(/_/g, ' ');
  }

  /**
   * Determine field display order
   * @param {Object} dataSource - Field values (keyValueMap or formData)
   * @param {Array} fieldSequence - Preferred field order
   * @param {Object} parsedHeaders - Parsed header data
   * @returns {Array} Ordered array of field IDs
   */
  static getFieldOrder(dataSource, fieldSequence, parsedHeaders) {
    const fieldIds = Object.keys(dataSource);

    // Use fieldSequence if available and complete
    if (fieldSequence && Array.isArray(fieldSequence) && fieldSequence.length > 0) {
      // Ensure all fields are included, add missing ones at the end
      const sequencedIds = fieldSequence.filter(id => fieldIds.includes(id));
      const missingIds = fieldIds.filter(id => !fieldSequence.includes(id));
      return [...sequencedIds, ...missingIds];
    }

    // Use header sequence if available
    if (Object.keys(parsedHeaders).length > 0) {
      return fieldIds.sort((a, b) => {
        const seqA = parsedHeaders[a]?.sequence || 999;
        const seqB = parsedHeaders[b]?.sequence || 999;
        return seqA - seqB;
      });
    }

    // Fallback to alphabetical order
    return fieldIds.sort();
  }

  /**
   * Format field value for display based on field type
   * @param {string} value - Raw field value
   * @param {string} type - Field type
   * @returns {string} Formatted value for display
   */
  static formatFieldValue(value, type) {
    if (!value || !value.toString().trim()) {
      return 'Not filled'; // Empty value indicator
    }

    const stringValue = value.toString().trim();

    switch (type) {
      case 'checkbox':
        return stringValue.toLowerCase() === 'true' ? 'Yes' : 'No';
        
      case 'date':
        try {
          const date = new Date(stringValue);
          if (!isNaN(date.getTime())) {
            return date.toLocaleDateString('en-GB', {
              day: 'numeric',
              month: 'short',
              year: 'numeric'
            });
          }
        } catch (e) {
          // Fall through to default
        }
        return stringValue;
        
      case 'time':
        // Handle time formats like "14:30" or "14:30:45"
        if (/^\d{2}:\d{2}(:\d{2})?$/.test(stringValue)) {
          return stringValue;
        }
        return stringValue;
        
      case 'textfield_uc':
      case 'uppercase_text':
        return stringValue.toUpperCase();
        
      case 'textfield_num':
      case 'textfield_dec':
      case 'number':
      case 'decimal':
        // Add thousand separators for large numbers
        const num = parseFloat(stringValue);
        if (!isNaN(num)) {
          return num.toLocaleString();
        }
        return stringValue;
        
      case 'scan':
      case 'qr_scan':
      case 'barcode':
      case 'scan_container':
      case 'shipping_container':
        return `📷 ${stringValue}`;
        
      case 'ocr':
      case 'ocr_text':
        return `📝 ${stringValue}`;
        
      default:
        return stringValue;
    }
  }

  /**
   * Get display icon for field type
   * @param {string} type - Field type
   * @returns {string} Icon/emoji for field type
   */
  static getFieldTypeIcon(type) {
    switch (type) {
      case 'textfield':
      case 'text':
      case 'textfield_uc':
      case 'uppercase_text':
        return '📝';
      case 'textfield_num':
      case 'textfield_dec':
      case 'number':
      case 'decimal':
      case 'scan_num':
      case 'numeric_scan':
        return '🔢';
      case 'date':
        return '📅';
      case 'time':
        return '⏰';
      case 'checkbox':
        return '☑️';
      case 'dropdown':
        return '📋';
      case 'scan':
      case 'qr_scan':
      case 'barcode':
      case 'scan_container':
      case 'shipping_container':
        return '📷';
      case 'ocr':
      case 'ocr_text':
        return '📝';
      default:
        return '📄';
    }
  }

  /**
   * Get field completion percentage
   * @param {Object} formData - Parsed form data
   * @returns {number} Completion percentage (0-100)
   */
  static getCompletionPercentage(formData) {
    if (!formData || formData.totalFields === 0) return 0;
    return Math.round((formData.completedFields / formData.totalFields) * 100);
  }
}

export default FormFieldParser;